import type { ComponentType, SVGProps } from "react";

import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "react-aria-components";

import { cn } from "@/utils/cn";

import { Tooltip, TooltipTrigger } from "../../tooltip";
import { isPathActive } from "../is-path-active";
import { NavItem } from "./nav-item";

interface NavGroupChild {
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
}

interface NavGroupProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  isCollapsed: boolean;
  items: NavGroupChild[];
  label: string;
  pathname: string;
}

export function NavGroup({
  icon: Icon,
  isCollapsed,
  items,
  label,
  pathname,
}: NavGroupProps) {
  const hasActiveChild = items.some((item) =>
    isPathActive(pathname, item.href),
  );
  const [isExpanded, setIsExpanded] = useState(hasActiveChild);

  return (
    <>
      <li>
        <TooltipTrigger delay={250}>
          <Button
            aria-expanded={isExpanded}
            className={cn(
              "flex w-full rounded-sm p-1.5 text-sm font-semibold text-text-secondary hover:bg-bg-active hover:text-text-primary outline-hidden",
              isCollapsed ? "justify-center px-2xl" : "gap-md px-lg",
            )}
            onPress={() => setIsExpanded((prev) => !prev)}
          >
            <Icon className="size-5 shrink-0 text-nav-item-icon-fg" />
            {!isCollapsed && (
              <>
                <span className="whitespace-nowrap">{label}</span>
                <ChevronRightIcon
                  className={cn(
                    "ml-auto size-5 shrink-0 text-gray-500 transition-transform duration-200",
                    isExpanded && "rotate-90",
                  )}
                />
              </>
            )}
          </Button>
          {isCollapsed && <Tooltip placement="right">{label}</Tooltip>}
        </TooltipTrigger>
      </li>

      {isExpanded && (
        <ul className="flex flex-col gap-1">
          {items.map((item) => (
            <NavItem
              href={item.href}
              icon={item.icon}
              isActive={isPathActive(pathname, item.href)}
              isCollapsed={isCollapsed}
              key={item.href}
              label={item.label}
            />
          ))}
        </ul>
      )}
    </>
  );
}
